"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { env } from "@/env";
import { EVENT, type EventName } from "@/lib/analytics-events";

// Single HTTP ingest path for all events. Authed events add a Bearer JWT;
// anonymous traffic relies on server-side visitor_id enrichment.

// Debug logging is opt-in via NEXT_PUBLIC_ANALYTICS_DEBUG env var or a
// localStorage flag so individual developers can flip it without redeploying.
// Production stays silent unless explicitly turned on.
function isDebugEnabled(): boolean {
  const envFlag = env.NEXT_PUBLIC_ANALYTICS_DEBUG;
  if (envFlag === "1" || envFlag === "true") return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("zom.analytics.debug") === "1";
  } catch {
    return false;
  }
}

function debugLog(...args: unknown[]) {
  if (!isDebugEnabled()) return;

  console.log("[analytics]", ...args);
}

function debugWarn(...args: unknown[]) {
  if (!isDebugEnabled()) return;

  console.warn("[analytics]", ...args);
}

const SESSION_STORAGE_KEY = "zom.analytics.sessionId";
const LAST_ACTIVITY_STORAGE_KEY = "zom.analytics.lastActivity";
const CONNECTION_ID_STORAGE_KEY = "zom.analytics.connectionId";
// Stamps the user_id we last fired `identify` for, so a refresh while logged in
// doesn't re-fire. Cleared on logout so the next login stitches again.
const IDENTIFIED_USER_STORAGE_KEY = "zom.analytics.identifiedUserId";
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 min — matches server-side session expiry sweep
const HEARTBEAT_INTERVAL_MS = 30 * 1000; // 30s — drops 6× from the WS-era 5s

// UUID v7: time-ordered, sortable — friendlier to Postgres B-tree indexes
// than v4. Inlined since crypto.randomUUID() only mints v4.
function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const ts = Date.now();
  // Big-endian 48-bit ms timestamp in the first 6 bytes.
  bytes[0] = Math.floor(ts / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ts / 2 ** 32) & 0xff;
  bytes[2] = (ts >>> 24) & 0xff;
  bytes[3] = (ts >>> 16) & 0xff;
  bytes[4] = (ts >>> 8) & 0xff;
  bytes[5] = ts & 0xff;
  // Version (7) in the high nibble of byte 6.
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  // Variant (10xx) in the high bits of byte 8.
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

function getOrCreateSessionId(): string {
  const now = Date.now();
  const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
  if (existing && lastActivity) {
    const elapsed = now - parseInt(lastActivity, 10);
    if (elapsed < INACTIVITY_TIMEOUT_MS) {
      sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
      return existing;
    }
  }
  const fresh = uuidv7();
  sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
  sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
  // A new session_id implies a new connection_id too.
  sessionStorage.removeItem(CONNECTION_ID_STORAGE_KEY);
  return fresh;
}

function getOrCreateConnectionId(): string {
  const existing = sessionStorage.getItem(CONNECTION_ID_STORAGE_KEY);
  if (existing) return existing;
  const fresh = uuidv7();
  sessionStorage.setItem(CONNECTION_ID_STORAGE_KEY, fresh);
  return fresh;
}

function touchActivity() {
  sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(Date.now()));
}

function pickUtm(search: string): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
} {
  const params = new URLSearchParams(search);
  const out: Record<string, string> = {};
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ]) {
    const v = params.get(key);
    if (v) out[key] = v;
  }
  return out;
}

function send(url: string, body: string, accessToken: string | null): boolean {
  // Authed events need an Authorization header — sendBeacon can't set custom
  // headers, so authed traffic falls through to fetch with `keepalive: true`
  // (which behaves like sendBeacon during page unload but supports headers).
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  if (typeof fetch === "undefined") {
    debugWarn("fetch unavailable, dropping beacon", { url });
    return false;
  }

  fetch(url, {
    method: "POST",
    headers,
    body,
    keepalive: true,
    credentials: "omit",
  })
    .then((res) => {
      if (!res.ok) {
        debugWarn(`POST ${url} responded ${res.status} ${res.statusText}`);
      } else {
        debugLog(`POST ${url} → ${res.status}`);
      }
    })
    .catch((err) => {
      debugWarn(`POST ${url} failed`, err);
    });
  return true;
}

export function useAnalyticsCollect() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const sessionIdRef = useRef<string>("");
  const connectionIdRef = useRef<string>("");
  const lastSentPathRef = useRef<string | null>(null);

  // Track auth token in a ref so the periodic heartbeat picks up changes
  // without needing a fresh interval on every login/logout.
  const accessToken =
    session?.access_token ?? session?.user?.access_token ?? null;
  const accessTokenRef = useRef<string | null>(accessToken);
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // Lazy-init in an effect so SSR doesn't touch window/sessionStorage.
  useEffect(() => {
    sessionIdRef.current = getOrCreateSessionId();
    connectionIdRef.current = getOrCreateConnectionId();
  }, []);

  const sendEvent = useCallback(
    (
      eventType: EventName | (string & {}),
      page?: string,
      metadata: Record<string, unknown> = {},
    ) => {
      const collectUrl = env.NEXT_PUBLIC_ANALYTICS_COLLECT_URL;
      if (!collectUrl) {
        debugWarn(
          `sendEvent("${eventType}") dropped — NEXT_PUBLIC_ANALYTICS_COLLECT_URL is not set`,
        );
        return;
      }
      if (typeof window === "undefined") return;
      debugLog(`sendEvent("${eventType}")`, { metadata, page: page ?? "auto" });

      // Defensive: if the effect hasn't run yet (very first render), populate now.
      if (!sessionIdRef.current) sessionIdRef.current = getOrCreateSessionId();
      if (!connectionIdRef.current)
        connectionIdRef.current = getOrCreateConnectionId();

      touchActivity();

      const utm = pickUtm(window.location.search);
      const referrer = document.referrer || undefined;

      const body = JSON.stringify({
        event_id: uuidv7(),
        event_type: eventType,
        analytics_session_id: sessionIdRef.current,
        connection_id: connectionIdRef.current,
        page: page ?? pathname,
        timestamp: new Date().toISOString(),
        referrer,
        ...utm,
        screen_width: window.screen?.width,
        screen_height: window.screen?.height,
        metadata,
      });

      send(
        `${collectUrl.replace(/\/$/, "")}/collect`,
        body,
        accessTokenRef.current,
      );
    },
    [pathname],
  );

  // One pageview per route change. Query strings are stripped — UTM lives in
  // its own columns; fbclid/gclid would otherwise explode the page list.
  useEffect(() => {
    if (!pathname) return;
    if (lastSentPathRef.current === pathname) return;
    lastSentPathRef.current = pathname;
    sendEvent(EVENT.pageview, pathname);
  }, [pathname, sendEvent]);

  // Authed-only heartbeat: keeps the server-side session 'active'. Anonymous
  // visitors don't beat (their pageviews are sufficient signal — saves ~99%
  // of beacon volume vs. heartbeating every marketing page).
  const isAuthed = !!accessToken;
  useEffect(() => {
    if (!isAuthed) return;
    const interval = setInterval(() => {
      sendEvent(EVENT.heartbeat);
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthed, sendEvent]);

  // `identify` stitches the pre-login anonymous session to the post-login
  // user_id. Fires once per login transition (not on refresh while authed, not
  // on every pageview). user_id itself is stamped server-side from the JWT —
  // this event just gives queries a row that joins analytics_session_id to a
  // user, so anonymous pageviews from earlier in the same session can be
  // attributed to the signup funnel.
  //
  // Guard correctness — three states to handle distinctly:
  //   status === "loading"          → useSession returns no data yet (e.g.
  //                                   page refresh while authed); MUST NOT
  //                                   clear the marker or we double-fire.
  //   status === "unauthenticated"  → real logout; clear the marker so the
  //                                   next login re-stitches.
  //   status === "authenticated"    → fire identify once per user_id; debounced
  //                                   via sessionStorage so silent JWT refresh
  //                                   doesn't double-fire either.
  const userId = session?.user?.id ?? null;
  const role = session?.user?.role ?? null;
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (status === "loading") return; // session not resolved yet
    if (status === "unauthenticated" || !userId) {
      sessionStorage.removeItem(IDENTIFIED_USER_STORAGE_KEY);
      return;
    }
    if (!accessToken) return; // token momentarily absent — wait for next render
    if (sessionStorage.getItem(IDENTIFIED_USER_STORAGE_KEY) === userId) return;
    sessionStorage.setItem(IDENTIFIED_USER_STORAGE_KEY, userId);
    sendEvent(EVENT.identify, undefined, role ? { role } : {});
  }, [status, userId, role, accessToken, sendEvent]);

  return {
    sendEvent,
    analyticsSessionId: sessionIdRef.current,
    connectionId: connectionIdRef.current,
  };
}
